/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  Text,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useAuth } from 'contexts/AuthContext';
import { filterRoutesByAccess } from 'utils/roleAccess';
import {
  CollapsedSidebarItem,
  CollapsedIconButton,
  collapsedIconInnerSx,
} from 'components/sidebar/components/CollapsedSidebarItem';

export function SidebarLinks(props) {
  let location = useLocation();
  let activeColor = useColorModeValue('gray.700', 'white');
  let inactiveColor = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.600',
  );
  let activeIcon = useColorModeValue('#00a4de', 'white');
  let textColor = useColorModeValue('#003064', 'white');
  let brandColor = useColorModeValue('#00a4de', 'brand.400');

  const { routes, collapsed } = props;
  const { roles, accesses } = useAuth();

  const visibleRoutes = useMemo(
    () => filterRoutesByAccess(routes, roles, accesses),
    [routes, roles, accesses],
  );

  // Estado para controlar qué acordeón está abierto
  const [openAccordion, setOpenAccordion] = useState(-1);

  const activeRoute = (routeName) => location.pathname.includes(routeName);

  // Función para manejar el toggle de acordeones
  const handleAccordionToggle = (index) => {
    setOpenAccordion(openAccordion === index ? -1 : index);
  };

  const renderCollapsedCategory = (route, index, accessibleItems) => (
    <CollapsedSidebarItem key={index}>
      <Box display="inline-flex" lineHeight={0}>
        <Popover placement="right" gutter={12} isLazy>
          <PopoverTrigger>
            <CollapsedIconButton
              bg="transparent"
              color={textColor}
              title={route.name}
              aria-label={route.name}
              _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
            >
              <Box as="span" sx={collapsedIconInnerSx} color="inherit">
                {route.icon}
              </Box>
            </CollapsedIconButton>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              w="220px"
              bg={useColorModeValue('white', 'navy.800')}
              borderColor={useColorModeValue('gray.200', 'whiteAlpha.100')}
              boxShadow="lg"
              zIndex={1500}
            >
              <PopoverBody p={2}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={useColorModeValue('gray.600', 'gray.300')}
                  mb={2}
                  px={1}
                >
                  {route.name}
                </Text>
                <VStack align="stretch" spacing={0}>
                  {accessibleItems.map((subRoute, subIndex) => (
                    <Box
                      key={subIndex}
                      as={NavLink}
                      to={subRoute.layout + subRoute.path}
                      display="flex"
                      alignItems="center"
                      px={3}
                      py={2}
                      borderRadius="md"
                      color={useColorModeValue('gray.700', 'white')}
                      fontSize="sm"
                      _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
                      bg={
                        activeRoute(subRoute.path.toLowerCase())
                          ? useColorModeValue('gray.100', 'whiteAlpha.200')
                          : 'transparent'
                      }
                    >
                      {subRoute.icon && (
                        <Box
                          me={2}
                          sx={collapsedIconInnerSx}
                          color={
                            activeRoute(subRoute.path.toLowerCase())
                              ? activeIcon
                              : textColor
                          }
                        >
                          {subRoute.icon}
                        </Box>
                      )}
                      <Text>{subRoute.name}</Text>
                    </Box>
                  ))}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    </CollapsedSidebarItem>
  );

  const renderCollapsedRoute = (route, index) => {
    const isActive = activeRoute(route.path.toLowerCase());
    return (
      <CollapsedSidebarItem key={index}>
        <CollapsedIconButton
          as={NavLink}
          to={route.layout + route.path}
          title={route.name}
          aria-label={route.name}
          bg={isActive ? useColorModeValue('gray.100', 'whiteAlpha.200') : 'transparent'}
          color={isActive ? activeIcon : textColor}
          textDecoration="none"
          _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
        >
          <Box as="span" sx={collapsedIconInnerSx} color="inherit">
            {route.icon}
          </Box>
        </CollapsedIconButton>
      </CollapsedSidebarItem>
    );
  };

  const createLinks = (routes) => {
    let accordionIndex = 0; // Contador para acordeones
    
    return routes.map((route, index) => {
      if (route.name === 'Sign In') return null;
      // Para rutas individuales (no `category/items`), también respetamos `showInNavbar`.
      if (route.showInNavbar === false) return null;

      if (route.layout === '/admin' || route.layout === '/auth') {
        if (route.category && route.items?.length) {
          const currentAccordionIndex = accordionIndex;
          accordionIndex++; // Incrementar para el próximo acordeón
          
          if (collapsed) {
            const accessibleItems = route.items.filter(
              (item) => item.showInNavbar !== false,
            );
            if (!accessibleItems.length) return null;
            return renderCollapsedCategory(route, index, accessibleItems);
          }
          
          return (
            <Box key={index} border="none" mb="4px">
              <Box border="none" borderRadius="12px">
                <Box 
                  py="12px"
                  px="16px"
                  cursor="pointer"
                  onClick={() => handleAccordionToggle(currentAccordionIndex)}
                  borderRadius="12px"
                  transition="all 0.2s"
                  _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
                >
                  <Flex w="100%" alignItems="center" justify="space-between">
                    <Flex alignItems="center">
                      <Box
                        color={textColor}
                        me="18px"
                      >
                        {route.icon}
                      </Box>
                      <Text
                        fontSize="md"
                        fontWeight="normal"
                        color={activeColor}
                      >
                        {route.name}
                      </Text>
                    </Flex>
                    <ChevronDownIcon 
                      transform={openAccordion === currentAccordionIndex ? 'rotate(180deg)' : 'rotate(0deg)'}
                      transition="transform 0.2s"
                      w={4}
                      h={4}
                      color={textColor}
                    />
                  </Flex>
                </Box>
                <Box 
                  overflow="hidden"
                  transition="all 0.3s ease-in-out"
                  maxH={openAccordion === currentAccordionIndex ? "500px" : "0px"}
                  opacity={openAccordion === currentAccordionIndex ? 1 : 0}
                >
                  <Box pt="8px" pb="16px">
                    <VStack align="start" spacing={2} pl={4}>
                      {route.items.map((subRoute, subIndex) => (
                        subRoute.showInNavbar !== false && (
                        <NavLink
                          key={subIndex}
                          to={subRoute.layout + subRoute.path}
                          style={{ width: '100%' }}
                        >
                          <Box
                            py="8px"
                            px="12px"
                            borderRadius="8px"
                            transition="background 0.2s"
                            _hover={{ bg: useColorModeValue('gray.100', 'whiteAlpha.200') }}
                            bg={
                              activeRoute(subRoute.path.toLowerCase())
                                ? useColorModeValue('gray.100', 'whiteAlpha.200')
                                : 'transparent'
                            }
                          >
                            <HStack spacing="12px">
                              <Flex w="100%" alignItems="center">
                                {subRoute.icon && (
                                  <Box
                                    color={
                                      activeRoute(subRoute.path.toLowerCase())
                                        ? activeIcon
                                        : textColor
                                    }
                                    me="18px"
                                  >
                                    {subRoute.icon}
                                  </Box>
                                )}
                                <Text
                                  me="auto"
                                  color={
                                    activeRoute(subRoute.path.toLowerCase())
                                      ? activeColor
                                      : textColor
                                  }
                                  fontWeight={
                                    activeRoute(subRoute.path.toLowerCase())
                                      ? 'bold'
                                      : 'normal'
                                  }
                                  fontSize="sm"
                                >
                                  {subRoute.name}
                                </Text>
                              </Flex>
                              <Box
                                h="36px"
                                w="4px"
                                bg={
                                  activeRoute(subRoute.path.toLowerCase())
                                    ? brandColor
                                    : 'transparent'
                                }
                                borderRadius="5px"
                              />
                            </HStack>
                          </Box>
                        </NavLink>)
                      ))}
                    </VStack>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        } else {
          // Ruta individual
          if (collapsed) {
            return renderCollapsedRoute(route, index);
          }
          
          return (
            <NavLink key={index} to={route.layout + route.path}>
              <Box>
                <HStack
                  spacing={
                    activeRoute(route.path.toLowerCase()) ? '22px' : '26px'
                  }
                  py="5px"
                  ps="10px"
                >
                  <Flex w="100%" alignItems="center">
                    {route.icon && (
                      <Box
                        color={
                          activeRoute(route.path.toLowerCase())
                            ? activeIcon
                            : textColor
                        }
                        me="18px"
                      >
                        {route.icon}
                      </Box>
                    )}
                    <Text
                      me="auto"
                      color={
                        activeRoute(route.path.toLowerCase())
                          ? activeColor
                          : textColor
                      }
                      fontWeight={
                        activeRoute(route.path.toLowerCase())
                          ? 'bold'
                          : 'normal'
                      }
                    >
                      {route.name}
                    </Text>
                  </Flex>
                  <Box
                    h="36px"
                    w="4px"
                    bg={
                      activeRoute(route.path.toLowerCase())
                        ? brandColor
                        : 'transparent'
                    }
                    borderRadius="5px"
                  />
                </HStack>
              </Box>
            </NavLink>
          );
        }
      }
    }).filter(Boolean);
  };

  if (collapsed) {
    return (
      <Flex
        direction="column"
        align="center"
        w="100%"
        gap="2px"
      >
        {createLinks(visibleRoutes)}
      </Flex>
    );
  }

  return <Box w="100%">{createLinks(visibleRoutes)}</Box>;
}

export default SidebarLinks;
